// src/components/Auth/Signup.js
// Updated:
// - Light theme to match LandingPage
// - Brand gradient (pink -> indigo)
// - Removed "free trial" language (aligns with "Free tier available. Upgrade anytime.")
// - Added logo + cleaner creator-friendly copy

import React, { useRef, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Mail, Lock, UserPlus, ArrowRight } from 'lucide-react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { executeRecaptcha } from '../../services/recaptcha';
import { api } from '../../services/apiClient';

export default function Signup() {
  const nameRef = useRef();
  const emailRef = useRef();
  const passwordRef = useRef();
  const { signup } = useAuth(); // kept for future (AuthContext), but backend route handles it now
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const planParam = new URLSearchParams(location.search).get('plan');

  async function handleSubmit(e) {
    e.preventDefault();
    setLoading(true);
    setError('');

    const email = emailRef.current.value.trim().toLowerCase();
    const password = passwordRef.current.value.trim();

    try {
      const recaptchaToken =
        typeof window.grecaptcha !== 'undefined' &&
        window.grecaptcha.enterprise &&
        typeof window.grecaptcha.enterprise.execute === 'function'
          ? await executeRecaptcha('signup')
          : 'MOCK_TOKEN';

      if (!recaptchaToken) {
        throw new Error('Security check failed. Please refresh the page and try again.');
      }

      const payload = {
        name: nameRef.current.value.trim(),
        email,
        password,
        recaptchaToken,
        // Optional: pass a plan hint for analytics / post-signup routing
        planHint: planParam || undefined,
      };

      const response = await api.post('/auth/signup-with-recaptcha', payload);

      if (!response.success) {
        throw new Error(response.error || 'Authentication failed due to server issue.');
      }

      // Keep your current onboarding flow
      navigate('/brand-setup', { replace: true });
    } catch (e) {
      console.error('Signup error:', e);
      let userMessage = 'Failed to create an account. Please check email format or password length.';
      if (String(e?.message || '').includes('email-already-in-use')) {
        userMessage = 'An account already exists with this email. Try logging in.';
      } else if (String(e?.message || '').includes('Security check failed')) {
        userMessage = 'Security check failed. Please try again.';
      }
      setError(userMessage);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4 py-12">
      {/* soft gradient accents to match LandingPage vibe */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
        <div className="absolute -top-24 -left-24 h-72 w-72 rounded-full bg-pink-200/40 blur-3xl" />
        <div className="absolute -bottom-28 -right-24 h-80 w-80 rounded-full bg-indigo-200/40 blur-3xl" />
      </div>

      <div className="relative w-full max-w-md">
        <div className="bg-white/90 backdrop-blur rounded-2xl shadow-2xl border border-gray-200 p-8">
          {/* Logo + Header */}
          <div className="text-center mb-8">
            <img
              src="/Infleuince_Landing.png"
              alt="Infleuince"
              className="h-10 w-auto mx-auto mb-5"
              loading="eager"
            />

            <div className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-gradient-to-r from-pink-500 to-indigo-500 text-white shadow-lg mb-4">
              <UserPlus className="w-5 h-5" />
            </div>

            <h2 className="text-3xl font-black text-gray-900">Create your account</h2>
            <p className="text-gray-600 text-sm mt-2">
              Start on the free tier — upgrade anytime.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                {error}
              </div>
            )}

            <div>
              <label
                htmlFor="name"
                className="block text-sm font-semibold text-gray-700 mb-2 flex items-center"
              >
                <UserPlus className="w-4 h-4 mr-2 text-gray-500" />
                Full Name
              </label>

              <input
                type="text"
                id="name"
                ref={nameRef}
                required
                disabled={loading}
                autoComplete="name"
                placeholder="Jennell Greer"
                className="w-full p-3 bg-white border border-gray-300 rounded-xl focus:ring-2 focus:ring-pink-400/50 focus:border-pink-500 text-gray-900 placeholder-gray-500 shadow-sm"
              />
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2 flex items-center">
                <Mail className="w-4 h-4 mr-2 text-gray-500" /> Email
              </label>
              <input
                type="email"
                id="email"
                ref={emailRef}
                required
                className="w-full p-3 bg-white border border-gray-300 rounded-xl focus:ring-2 focus:ring-pink-400/50 focus:border-pink-500 text-gray-900 placeholder-gray-500 shadow-sm"
                placeholder="you@creator.com"
                disabled={loading}
                autoComplete="email"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-semibold text-gray-700 mb-2 flex items-center">
                <Lock className="w-4 h-4 mr-2 text-gray-500" /> Password
              </label>
              <input
                type="password"
                id="password"
                ref={passwordRef}
                required
                className="w-full p-3 bg-white border border-gray-300 rounded-xl focus:ring-2 focus:ring-pink-400/50 focus:border-pink-500 text-gray-900 placeholder-gray-500 shadow-sm"
                placeholder="Minimum 6 characters"
                disabled={loading}
                autoComplete="new-password"
              />
              <p className="mt-2 text-xs text-gray-500">
                Tip: use a passphrase you'll remember.
              </p>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full rounded-xl bg-gradient-to-r from-pink-500 to-indigo-500 text-white py-3.5 font-extrabold shadow-lg hover:brightness-105 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {loading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Creating your account…
                </>
              ) : (
                <>
                  Create account
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>

            {/* Legal note */}
            <p className="text-xs text-gray-500 leading-relaxed">
              By creating an account, you agree to our{' '}
              <Link to="/terms-of-service" className="text-pink-700 font-semibold hover:underline">Terms</Link>
              {' '}and{' '}
              <Link to="/privacy-policy" className="text-pink-700 font-semibold hover:underline">Privacy Policy</Link>.
            </p>
          </form>

          <div className="mt-7 pt-6 border-t border-gray-100 text-center">
            <p className="text-sm text-gray-600">
              Already have an account?{' '}
              <Link to="/login" className="text-pink-700 hover:text-pink-800 font-extrabold">
                Log in
              </Link>
            </p>
          </div>
        </div>

        {/* optional: small badge */}
        <div className="mt-5 text-center text-xs text-gray-500">
          {planParam ? (
            <span>
              You selected the <span className="font-bold text-gray-700">{planParam}</span> plan.
              {' '}You can change this later.
            </span>
          ) : (
            <span>New here? You'll finish setup in under 2 minutes.</span>
          )}
        </div>
      </div>
    </div>
  );
}